import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { useGetPaymentLocationDetails } from "api/queries/user/useGetPaymentLocationDetails";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import PaymentLocationFormField from "./PaymentLocationFormField";
import usePaymentLocationLabels from "./hooks/usePaymentLocationLabels";
import usePaymentLocationFields from "./hooks/usePaymentLocationFields";
import usePaymentLocationMutations from "./hooks/usePaymentLocationMutations";
import { DEFAULT_PAYMENT_LOCATION_VALUES } from "./constants";

interface AddEditPaymentLocationDialogProps {
  showEditLocationDialog: boolean;
  closeEditLocationDialog: () => void;
  selectedLocationReferenceNumber: string;
  refetchPaymentLocations: () => void;
  viewOnly?: boolean;
}

export default function AddEditPaymentLocationDialog({
  showEditLocationDialog,
  closeEditLocationDialog,
  selectedLocationReferenceNumber,
  refetchPaymentLocations,
  viewOnly,
}: AddEditPaymentLocationDialogProps) {
  const methods = useForm({
    defaultValues: DEFAULT_PAYMENT_LOCATION_VALUES,
  });
  const {
    watch,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
    control,
    setValue,
  } = methods;

  const closeDialog = () => {
    reset();
    closeEditLocationDialog();
  };

  const { getIntercomOptions, getDefaultIncotermOptions, getDialogTitle } = usePaymentLocationLabels({
    viewOnly,
    selectedLocationReferenceNumber,
  });

  const { data: paymentLocationDetails }: any = useGetPaymentLocationDetails(selectedLocationReferenceNumber!, {
    enabled: !!selectedLocationReferenceNumber,
  });

  const { isLoading, handleSubmitPaymentLocation } = usePaymentLocationMutations({
    selectedLocationReferenceNumber,
    refetchPaymentLocations,
    closeDialog,
    setError,
    clearErrors,
  });

  const { paymentLocationFields, invoiceDetailFields } = usePaymentLocationFields({
    viewOnly,
    getIntercomOptions,
    getDefaultIncotermOptions,
    watch,
    errors,
    control,
    setValue,
    clearErrors,
  });

  useEffect(() => {
    if (!selectedLocationReferenceNumber) {
      reset(DEFAULT_PAYMENT_LOCATION_VALUES);
      return;
    }
    const formattedFreightModes = paymentLocationDetails?.freight_modes?.reduce((acc: any, mode: any) => {
      acc[mode?.value] = mode?.is_active;
      return acc;
    }, {});

    reset({ ...paymentLocationDetails, freight_modes: formattedFreightModes });
  }, [paymentLocationDetails, reset]);

  return (
    <Dialog show={showEditLocationDialog} size="md" onHide={closeDialog} title={getDialogTitle()}>
      <FormProvider {...methods}>
        {paymentLocationFields.map(({ label, children, required = false }, index) => (
          <PaymentLocationFormField key={index} label={label} required={required}>
            {children}
          </PaymentLocationFormField>
        ))}

        <div className="text-lg font-semibold mb-lg mt-md">Bill to</div>
        {invoiceDetailFields.map(({ label, children, required = false }, index) => (
          <PaymentLocationFormField key={index} label={label} required={required}>
            {children}
          </PaymentLocationFormField>
        ))}

        {!viewOnly && (
          <Button
            isLoading={isLoading}
            className="rounded-pill mt-3 ml-auto"
            onClick={handleSubmit(handleSubmitPaymentLocation)}
          >
            <FormattedMessage id="general.submit" defaultMessage="Submit" />
          </Button>
        )}
      </FormProvider>
    </Dialog>
  );
}

AddEditPaymentLocationDialog.defaultProps = {
  viewOnly: false,
};
